import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, CheckCircle2 } from "lucide-react";
import BlueHeronIcon from "@/assets/blue-heron-icon.svg";
import { NAVY, ACCENT } from "@/lib/siteTheme";
import Turnstile, { type TurnstileHandle } from "@/components/Turnstile";

// Re-theme the shadcn form primitives (Input, Button, Card, Checkbox, etc.)
// used throughout this page to match the site's navy/cream/gold redesign,
// without needing to touch every individual field. Scoped to .bh-onboarding
// so it doesn't affect the global theme other untouched pages still rely on.
const ONBOARDING_THEME_CSS = `
  .bh-onboarding, .bh-onboarding * {
    font-family: 'Archivo', sans-serif;
  }
  .bh-onboarding {
    --background: 38 41% 95%;
    --foreground: 208 47% 13%;
    --card: 0 0% 100%;
    --card-foreground: 208 47% 13%;
    --popover: 0 0% 100%;
    --popover-foreground: 208 47% 13%;
    --primary: 213 66% 9%;
    --primary-foreground: 38 41% 95%;
    --secondary: 38 25% 91%;
    --secondary-foreground: 213 66% 9%;
    --muted: 38 25% 91%;
    --muted-foreground: 208 15% 45%;
    --accent: 38 25% 91%;
    --accent-foreground: 213 66% 9%;
    --border: 213 25% 88%;
    --input: 213 25% 88%;
    --ring: 36 79% 57%;
    --radius: 1rem;
  }
`;

const STEP_LABELS = [
  "Business & Brand",
  "Website Goals",
  "Design Preferences",
  "Pages & Content",
  "Features & Functionality",
  "Domain, Hosting & Email",
  "SEO & Marketing",
  "Final Details",
];

interface FormData {
  // Contact info
  fullName: string;
  email: string;
  phone: string;
  // Step 1
  businessName: string;
  hasLogo: string;
  logoFiles: File[];
  brandGuidelines: string;
  businessDescription: string;
  idealCustomer: string;
  competitiveDifference: string;
  // Step 2
  websiteGoals: string[];
  websiteGoalsOther: string;
  visitorActions: string[];
  visitorActionsOther: string;
  hasCurrentWebsite: string;
  currentWebsiteUrl: string;
  currentWebsiteProblems: string;
  // Step 3
  inspirationSites: string;
  brandStyle: string[];
  brandStyleOther: string;
  colorFontPreferences: string;
  doNotWant: string;
  // Step 4
  pagesNeeded: string[];
  hiddenPages: string;
  contentReadiness: string;
  contentFiles: File[];
  // Step 5
  needsScheduling: string;
  needsEcommerce: string;
  needsContactForm: string;
  needsMultiLanguage: string;
  otherFeatures: string;
  // Step 6
  ownsDomain: string;
  domainName: string;
  domainRegistrar: string;
  domainRegistrarOther: string;
  wantedDomain: string;
  needsEmail: string;
  emailAddresses: string;
  // Step 7
  seoKeywords: string;
  hasGoogleBusiness: string;
  setupAnalytics: string;
  marketingServices: string[];
  // Step 8
  decisionMakers: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  preferredContact: string;
  anythingElse: string;
}

const initialFormData: FormData = {
  fullName: "",
  email: "",
  phone: "",
  businessName: "",
  hasLogo: "",
  logoFiles: [],
  brandGuidelines: "",
  businessDescription: "",
  idealCustomer: "",
  competitiveDifference: "",
  websiteGoals: [],
  websiteGoalsOther: "",
  visitorActions: [],
  visitorActionsOther: "",
  hasCurrentWebsite: "",
  currentWebsiteUrl: "",
  currentWebsiteProblems: "",
  inspirationSites: "",
  brandStyle: [],
  brandStyleOther: "",
  colorFontPreferences: "",
  doNotWant: "",
  pagesNeeded: [],
  hiddenPages: "",
  contentReadiness: "",
  contentFiles: [],
  needsScheduling: "",
  needsEcommerce: "",
  needsContactForm: "Yes",
  needsMultiLanguage: "",
  otherFeatures: "",
  ownsDomain: "",
  domainName: "",
  domainRegistrar: "",
  domainRegistrarOther: "",
  wantedDomain: "",
  needsEmail: "",
  emailAddresses: "",
  seoKeywords: "",
  hasGoogleBusiness: "",
  setupAnalytics: "Yes",
  marketingServices: [],
  decisionMakers: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  preferredContact: "",
  anythingElse: "",
};

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const [highestStep, setHighestStep] = useState(1);

  // Shared head block for this route. noindex preserves the original intent
  // (the onboarding form was deliberately excluded from search).
  const seo = (
    <Seo
      title="Website Onboarding | Blue Heron Marketing"
      description="Client onboarding form for new Blue Heron Marketing website projects."
      path="/onboarding"
      noindex
    />
  );

  const progress = (currentStep / 8) * 100;

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleCheckbox = (field: keyof FormData, value: string) => {
    const arr = formData[field] as string[];
    if (arr.includes(value)) {
      updateField(field, arr.filter((v) => v !== value));
    } else {
      updateField(field, [...arr, value]);
    }
  };

  const validateStep = (step: number): boolean => {
    const errs: Record<string, string> = {};
    switch (step) {
      case 1:
        if (!formData.fullName.trim()) errs.fullName = "Required";
        if (!formData.email.trim()) errs.email = "Required";
        if (!formData.phone.trim()) errs.phone = "Required";
        if (!formData.businessName.trim()) errs.businessName = "Required";
        if (!formData.hasLogo) errs.hasLogo = "Required";
        if (!formData.businessDescription.trim()) errs.businessDescription = "Required";
        if (!formData.idealCustomer.trim()) errs.idealCustomer = "Required";
        if (!formData.competitiveDifference.trim()) errs.competitiveDifference = "Required";
        break;
      case 2:
        if (formData.websiteGoals.length === 0) errs.websiteGoals = "Pick at least one";
        if (formData.visitorActions.length === 0) errs.visitorActions = "Pick at least one";
        if (!formData.hasCurrentWebsite) errs.hasCurrentWebsite = "Required";
        break;
      case 4:
        if (formData.pagesNeeded.length === 0) errs.pagesNeeded = "Pick at least one";
        if (!formData.contentReadiness) errs.contentReadiness = "Required";
        break;
      case 8:
        if (!formData.decisionMakers.trim()) errs.decisionMakers = "Required";
        if (!formData.contactName.trim()) errs.contactName = "Required";
        if (!formData.contactEmail.trim()) errs.contactEmail = "Required";
        if (!formData.contactPhone.trim()) errs.contactPhone = "Required";
        if (!formData.preferredContact) errs.preferredContact = "Required";
        break;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    const next = currentStep + 1;
    setCurrentStep(next);
    setHighestStep((prev) => Math.max(prev, next));
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
    window.scrollTo(0, 0);
  };

  const handleStepClick = (step: number) => {
    if (step <= highestStep) {
      setCurrentStep(step);
      window.scrollTo(0, 0);
    }
  };

  const buildAllSummary = (): string => {
    const lines: string[] = [];
    const add = (label: string, value: string | string[]) => {
      const v = Array.isArray(value) ? value.join(", ") : value;
      if (v && v.trim()) lines.push(`${label}: ${v.trim()}`);
    };

    add("Full Name", formData.fullName);
    add("Email", formData.email);
    add("Phone", formData.phone);
    add("Business Name", formData.businessName);
    add("Has Logo/Brand Guidelines", formData.hasLogo);
    if (formData.hasLogo === "Yes") {
      add("Logo Files", formData.logoFiles.map((f) => f.name));
      add("Brand Guidelines", formData.brandGuidelines);
    }
    add("Business Description", formData.businessDescription);
    add("Ideal Customer", formData.idealCustomer);
    add("Competitive Difference", formData.competitiveDifference);
    add("Website Goals", formData.websiteGoals);
    if (formData.websiteGoalsOther) add("Website Goals (Other)", formData.websiteGoalsOther);
    add("Visitor Actions", formData.visitorActions);
    if (formData.visitorActionsOther) add("Visitor Actions (Other)", formData.visitorActionsOther);
    add("Has Current Website", formData.hasCurrentWebsite);
    if (formData.hasCurrentWebsite === "Yes") {
      add("Current Website URL", formData.currentWebsiteUrl);
      add("Current Website Problems", formData.currentWebsiteProblems);
    }
    add("Inspiration Sites", formData.inspirationSites);
    add("Brand Style", formData.brandStyle);
    if (formData.brandStyleOther) add("Brand Style (Other)", formData.brandStyleOther);
    add("Color/Font Preferences", formData.colorFontPreferences);
    add("Do Not Want", formData.doNotWant);
    add("Pages Needed", formData.pagesNeeded);
    add("Hidden Pages", formData.hiddenPages);
    add("Content Readiness", formData.contentReadiness);
    if (formData.contentFiles.length > 0) add("Content Files", formData.contentFiles.map((f) => f.name));
    add("Needs Scheduling", formData.needsScheduling);
    add("Needs E-Commerce", formData.needsEcommerce);
    add("Needs Contact Form", formData.needsContactForm);
    add("Needs Multi-Language", formData.needsMultiLanguage);
    add("Other Features", formData.otherFeatures);
    add("Owns Domain", formData.ownsDomain);
    if (formData.ownsDomain === "Yes") {
      add("Domain Name", formData.domainName);
      add("Domain Registrar", formData.domainRegistrar === "Other" ? formData.domainRegistrarOther : formData.domainRegistrar);
    } else {
      add("Wanted Domain", formData.wantedDomain);
    }
    add("Needs Email", formData.needsEmail);
    if (formData.needsEmail === "Yes") add("Email Addresses", formData.emailAddresses);
    add("SEO Keywords", formData.seoKeywords);
    add("Has Google Business Profile", formData.hasGoogleBusiness);
    add("Setup Analytics", formData.setupAnalytics);
    add("Marketing Services", formData.marketingServices);
    add("Decision Makers", formData.decisionMakers);
    add("Contact Name", formData.contactName);
    add("Contact Email", formData.contactEmail);
    add("Contact Phone", formData.contactPhone);
    add("Preferred Contact", formData.preferredContact);
    add("Anything Else", formData.anythingElse);

    return lines.join("\n");
  };

  const uploadFiles = async (files: File[], folder: string): Promise<string[]> => {
    const urls: string[] = [];
    const timestamp = Date.now();
    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${folder}/${timestamp}_${safeName}`;
      const { data, error } = await supabase.storage
        .from("onboarding-files")
        .upload(path, file);
      if (!error && data) {
        const { data: urlData } = supabase.storage
          .from("onboarding-files")
          .getPublicUrl(data.path);
        urls.push(urlData.publicUrl);
      }
    }
    return urls;
  };

  const handleSubmit = async () => {
    if (!validateStep(8)) return;
    if (!turnstileToken) {
      setVerifyError(true);
      return;
    }
    setVerifyError(false);
    setIsSubmitting(true);

    // Upload files to storage
    const submissionFolder = `${formData.businessName.replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}`;
    let logoUrls: string[] = [];
    let contentUrls: string[] = [];

    try {
      if (formData.logoFiles.length > 0) {
        logoUrls = await uploadFiles(formData.logoFiles, `${submissionFolder}/logos`);
      }
      if (formData.contentFiles.length > 0) {
        contentUrls = await uploadFiles(formData.contentFiles, `${submissionFolder}/content`);
      }
    } catch (e) {
      // File upload failed - continue with submission
    }

    const allSummary = buildAllSummary() +
      (logoUrls.length > 0 ? `\nLogo File URLs:\n${logoUrls.join("\n")}` : "") +
      (contentUrls.length > 0 ? `\nContent File URLs:\n${contentUrls.join("\n")}` : "");

    // Flatten each uploaded file's public URL into its own top-level field
    // (logo_file_url_1, logo_file_url_2, ...) since GHL's webhook field
    // mapping works off flat values, not JSON arrays like logoFileUrls below.
    const numberedUrlFields: Record<string, string> = {};
    logoUrls.forEach((url, i) => { numberedUrlFields[`logo_file_url_${i + 1}`] = url; });
    contentUrls.forEach((url, i) => { numberedUrlFields[`content_file_url_${i + 1}`] = url; });

    // Send to HighLevel via a server-side function (not directly from the
    // browser) so delivery isn't at the mercy of the visitor's ad blocker or
    // privacy extension silently dropping a request to leadconnectorhq.com.
    try {
      const payload = {
        ...formData,
        logoFiles: formData.logoFiles.map((f) => f.name),
        logoFileUrls: logoUrls,
        contentFiles: formData.contentFiles.map((f) => f.name),
        contentFileUrls: contentUrls,
        ...numberedUrlFields,
        all_file_urls: [...logoUrls, ...contentUrls].join("\n"),
        all: allSummary,
        turnstileToken,
      };
      const res = await fetch("/api/submit-onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 403) {
        // Bot check rejected (e.g. token expired) — let the visitor retry.
        setVerifyError(true);
        turnstileRef.current?.reset();
        setIsSubmitting(false);
        return;
      }
      if (!res.ok) {
        console.error("Onboarding webhook proxy returned", res.status);
      }
    } catch (e) {
      console.error("Onboarding webhook proxy request failed:", e);
      // Database backup below still runs regardless.
    }

    // Save to database as backup
    try {
      await supabase.from("onboarding_submissions").insert({
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        business_name: formData.businessName,
        has_logo: formData.hasLogo,
        brand_guidelines: formData.brandGuidelines,
        business_description: formData.businessDescription,
        ideal_customer: formData.idealCustomer,
        competitive_difference: formData.competitiveDifference,
        website_goals: formData.websiteGoals,
        website_goals_other: formData.websiteGoalsOther,
        visitor_actions: formData.visitorActions,
        visitor_actions_other: formData.visitorActionsOther,
        has_current_website: formData.hasCurrentWebsite,
        current_website_url: formData.currentWebsiteUrl,
        current_website_problems: formData.currentWebsiteProblems,
        inspiration_sites: formData.inspirationSites,
        brand_style: formData.brandStyle,
        brand_style_other: formData.brandStyleOther,
        color_font_preferences: formData.colorFontPreferences,
        do_not_want: formData.doNotWant,
        pages_needed: formData.pagesNeeded,
        hidden_pages: formData.hiddenPages,
        content_readiness: formData.contentReadiness,
        needs_scheduling: formData.needsScheduling,
        needs_ecommerce: formData.needsEcommerce,
        needs_contact_form: formData.needsContactForm,
        needs_multi_language: formData.needsMultiLanguage,
        other_features: formData.otherFeatures,
        owns_domain: formData.ownsDomain,
        domain_name: formData.domainName,
        domain_registrar: formData.domainRegistrar === "Other" ? formData.domainRegistrarOther : formData.domainRegistrar,
        wanted_domain: formData.wantedDomain,
        needs_email: formData.needsEmail,
        email_addresses: formData.emailAddresses,
        seo_keywords: formData.seoKeywords,
        has_google_business: formData.hasGoogleBusiness,
        setup_analytics: formData.setupAnalytics,
        marketing_services: formData.marketingServices,
        decision_makers: formData.decisionMakers,
        contact_name: formData.contactName,
        contact_email: formData.contactEmail,
        contact_phone: formData.contactPhone,
        preferred_contact: formData.preferredContact,
        anything_else: formData.anythingElse,
        all_summary: allSummary,
      });
    } catch (e) {
      console.error("Onboarding database backup insert failed:", e);
    }

    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  const ErrorMsg = ({ field }: { field: string }) =>
    errors[field] ? <p className="text-destructive text-sm mt-1">{errors[field]}</p> : null;

  const RadioField = ({
    field,
    options,
    label,
  }: {
    field: keyof FormData;
    options: string[];
    label: string;
  }) => (
    <div className="space-y-2">
      <Label className="text-base font-semibold">{label}</Label>
      <RadioGroup value={formData[field] as string} onValueChange={(v) => updateField(field, v)}>
        {options.map((opt) => (
          <div key={opt} className="flex items-center space-x-2">
            <RadioGroupItem value={opt} id={`${String(field)}-${opt}`} />
            <Label htmlFor={`${String(field)}-${opt}`} className="font-normal cursor-pointer">
              {opt}
            </Label>
          </div>
        ))}
      </RadioGroup>
      <ErrorMsg field={String(field)} />
    </div>
  );

  const CheckboxGroup = ({
    field,
    options,
    label,
    hasOther,
    otherField,
    maxSelections,
  }: {
    field: keyof FormData;
    options: string[];
    label: string;
    hasOther?: boolean;
    otherField?: keyof FormData;
    maxSelections?: number;
  }) => {
    const selected = formData[field] as string[];
    return (
      <div className="space-y-2">
        <Label className="text-base font-semibold">
          {label}
          {maxSelections && <span className="text-muted-foreground font-normal text-sm ml-1">(pick up to {maxSelections})</span>}
        </Label>
        <div className="space-y-2">
          {options.map((opt) => (
            <div key={opt} className="flex items-center space-x-2">
              <Checkbox
                id={`${String(field)}-${opt}`}
                checked={selected.includes(opt)}
                disabled={maxSelections ? !selected.includes(opt) && selected.length >= maxSelections : false}
                onCheckedChange={() => toggleCheckbox(field, opt)}
              />
              <Label htmlFor={`${String(field)}-${opt}`} className="font-normal cursor-pointer">
                {opt}
              </Label>
            </div>
          ))}
          {hasOther && otherField && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id={`${String(field)}-other`}
                checked={selected.includes("Other")}
                disabled={maxSelections ? !selected.includes("Other") && selected.length >= maxSelections : false}
                onCheckedChange={() => toggleCheckbox(field, "Other")}
              />
              <div className="flex-1">
                <Label htmlFor={`${String(field)}-other`} className="font-normal cursor-pointer">
                  Other
                </Label>
                {selected.includes("Other") && (
                  <Input
                    className="mt-1"
                    placeholder="Please specify..."
                    value={formData[otherField] as string}
                    onChange={(e) => updateField(otherField, e.target.value)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <ErrorMsg field={String(field)} />
      </div>
    );
  };

  const FileUpload = ({
    field,
    label,
    multiple,
  }: {
    field: keyof FormData;
    label: string;
    multiple?: boolean;
  }) => {
    const files = formData[field] as File[];
    return (
      <div className="space-y-2">
        <Label className="text-base font-semibold">{label}</Label>
        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
          <Upload className="w-8 h-8 text-muted-foreground mb-2" />
          <span className="text-sm text-muted-foreground">Click to upload or drag files here</span>
          <input
            type="file"
            className="hidden"
            multiple={multiple}
            onChange={(e) => {
              const newFiles = Array.from(e.target.files || []);
              updateField(field, multiple ? [...files, ...newFiles] : newFiles);
            }}
          />
        </label>
        {files.length > 0 && (
          <div className="space-y-1">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-1">
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive ml-2"
                  onClick={() => updateField(field, files.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isSubmitted) {
    return (
      <div className="bh-onboarding min-h-screen bg-background flex items-center justify-center px-4">
        {seo}
        <div className="max-w-lg text-center space-y-6">
          <CheckCircle2 className="w-16 h-16 mx-auto" style={{ color: ACCENT }} />
          <h1 className="font-black text-primary" style={{ fontSize: "clamp(28px,4vw,40px)", letterSpacing: "-0.02em" }}>
            Thanks!
          </h1>
          <p className="text-lg text-foreground">
            We've received your info and will be in touch within 1–2 business days to kick things off.
          </p>
          <p className="text-sm text-muted-foreground">
            If you have questions, email us at{" "}
            <a href="mailto:help@blueheronstrategy.com" className="text-primary underline">
              help@blueheronstrategy.com
            </a>
          </p>
        </div>
        <style dangerouslySetInnerHTML={{ __html: ONBOARDING_THEME_CSS }} />
      </div>
    );
  }

  return (
    <div className="bh-onboarding min-h-screen bg-background">
      {seo}
      {/* Header */}
      <header className="py-6 px-4" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto flex justify-center">
          <img src={BlueHeronIcon} alt="Blue Heron" className="h-10 w-10 brightness-0 invert" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-black text-primary mb-2" style={{ fontSize: "clamp(28px,4vw,40px)", letterSpacing: "-0.02em" }}>
            Website Onboarding
          </h1>
          <p className="text-muted-foreground">
            Help us build the perfect website for your business. This should take about 10–15 minutes.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-primary">
              Step {currentStep} of 8: {STEP_LABELS[currentStep - 1]}
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: ACCENT }}
            />
          </div>
          {/* Step dots - visible on desktop */}
          <div className="hidden md:flex justify-between mt-3">
            {STEP_LABELS.map((label, i) => {
              const step = i + 1;
              const completed = step < currentStep;
              const active = step === currentStep;
              const clickable = step <= highestStep;
              return (
                <button
                  key={step}
                  onClick={() => handleStepClick(step)}
                  disabled={!clickable}
                  className={`flex flex-col items-center group ${clickable ? "cursor-pointer" : "cursor-default"}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      completed || active
                        ? ""
                        : clickable
                        ? "bg-muted text-muted-foreground hover:bg-primary/10"
                        : "bg-muted text-muted-foreground/50"
                    }`}
                    style={completed || active ? { background: ACCENT, color: NAVY } : undefined}
                  >
                    {completed ? "✓" : step}
                  </div>
                  <span
                    className={`text-[10px] mt-1 max-w-[80px] text-center leading-tight ${
                      active ? "text-primary font-semibold" : "text-muted-foreground"
                    }`}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8 space-y-6">
            {/* Step 1 */}
            {currentStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Full Name *</Label>
                  <Input
                    value={formData.fullName}
                    onChange={(e) => updateField("fullName", e.target.value)}
                    placeholder="Your full name"
                  />
                  <ErrorMsg field="fullName" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="you@example.com"
                  />
                  <ErrorMsg field="email" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Phone *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                  <ErrorMsg field="phone" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Business Name *</Label>
                  <Input
                    value={formData.businessName}
                    onChange={(e) => updateField("businessName", e.target.value)}
                    placeholder="Your business name"
                  />
                  <ErrorMsg field="businessName" />
                </div>

                <RadioField
                  field="hasLogo"
                  options={["Yes", "No"]}
                  label="Do you have a logo and brand guidelines? *"
                />

                {formData.hasLogo === "Yes" && (
                  <>
                    <FileUpload field="logoFiles" label="Upload your logo" multiple />
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Describe your brand guidelines</Label>
                      <Textarea
                        value={formData.brandGuidelines}
                        onChange={(e) => updateField("brandGuidelines", e.target.value)}
                        placeholder="Colors, fonts, imagery style..."
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Short description of your business *</Label>
                  <Textarea
                    value={formData.businessDescription}
                    onChange={(e) => updateField("businessDescription", e.target.value)}
                    placeholder="Your elevator pitch — what do you do and for whom?"
                  />
                  <ErrorMsg field="businessDescription" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Who is your ideal customer? *</Label>
                  <Textarea
                    value={formData.idealCustomer}
                    onChange={(e) => updateField("idealCustomer", e.target.value)}
                  />
                  <ErrorMsg field="idealCustomer" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">What makes you different from competitors? *</Label>
                  <Textarea
                    value={formData.competitiveDifference}
                    onChange={(e) => updateField("competitiveDifference", e.target.value)}
                  />
                  <ErrorMsg field="competitiveDifference" />
                </div>
              </>
            )}

            {/* Step 2 */}
            {currentStep === 2 && (
              <>
                <CheckboxGroup
                  field="websiteGoals"
                  label="Main goals for your website *"
                  options={[
                    "Generate leads",
                    "Showcase services",
                    "Build credibility/trust",
                    "Sell products online",
                    "Provide information",
                  ]}
                  hasOther
                  otherField="websiteGoalsOther"
                />

                <CheckboxGroup
                  field="visitorActions"
                  label="What should visitors do on your site? *"
                  options={[
                    "Call us",
                    "Fill out a contact form",
                    "Book an appointment",
                    "Request a quote",
                    "Purchase something",
                  ]}
                  hasOther
                  otherField="visitorActionsOther"
                />

                <RadioField
                  field="hasCurrentWebsite"
                  options={["Yes", "No"]}
                  label="Do you have a current website? *"
                />

                {formData.hasCurrentWebsite === "Yes" && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Current website URL</Label>
                      <Input
                        value={formData.currentWebsiteUrl}
                        onChange={(e) => updateField("currentWebsiteUrl", e.target.value)}
                        placeholder="https://www.example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">What problems do you want solved with the new site?</Label>
                      <Textarea
                        value={formData.currentWebsiteProblems}
                        onChange={(e) => updateField("currentWebsiteProblems", e.target.value)}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Step 3 */}
            {currentStep === 3 && (
              <>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Paste URLs of 2–3 websites you like and tell us what you like about them
                  </Label>
                  <Textarea
                    value={formData.inspirationSites}
                    onChange={(e) => updateField("inspirationSites", e.target.value)}
                    placeholder="Example: www.example.com — I like the clean layout and how easy it is to find info"
                    className="min-h-[100px]"
                  />
                </div>

                <CheckboxGroup
                  field="brandStyle"
                  label="What style fits your brand?"
                  options={[
                    "Modern",
                    "Clean",
                    "Bold",
                    "Elegant",
                    "Professional",
                    "Rustic",
                    "Friendly/Approachable",
                  ]}
                  hasOther
                  otherField="brandStyleOther"
                  maxSelections={3}
                />

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Specific colors, fonts, or imagery you want used?</Label>
                  <Textarea
                    value={formData.colorFontPreferences}
                    onChange={(e) => updateField("colorFontPreferences", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Anything you do NOT want on your site?</Label>
                  <Textarea
                    value={formData.doNotWant}
                    onChange={(e) => updateField("doNotWant", e.target.value)}
                    placeholder="e.g., stock photos, dark backgrounds, auto-playing video"
                  />
                </div>
              </>
            )}

            {/* Step 4 */}
            {currentStep === 4 && (
              <>
                <CheckboxGroup
                  field="pagesNeeded"
                  label="Which pages do you need? *"
                  options={[
                    "Home",
                    "About Us",
                    "Services (single page)",
                    "Individual Service Pages",
                    "Products / E-Commerce",
                    "Blog",
                    "Testimonials / Reviews",
                    "FAQ",
                    "Contact",
                    "Privacy Policy / Terms of Service",
                    "Careers",
                    "Landing Pages for Ads",
                    "Multiple Location Pages",
                  ]}
                />

                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Any pages that should be hidden / only accessible via direct link?
                  </Label>
                  <Textarea
                    value={formData.hiddenPages}
                    onChange={(e) => updateField("hiddenPages", e.target.value)}
                  />
                </div>

                <RadioField
                  field="contentReadiness"
                  options={[
                    "I have all my text, images, and videos ready",
                    "I have some content but need help filling gaps",
                    "I need help creating everything",
                  ]}
                  label="How ready is your content? *"
                />

                <FileUpload
                  field="contentFiles"
                  label="Upload any content you have ready — photos, copy docs, videos, etc."
                  multiple
                />
              </>
            )}

            {/* Step 5 */}
            {currentStep === 5 && (
              <>
                <RadioField
                  field="needsScheduling"
                  options={["Yes", "No", "Not sure"]}
                  label="Do you need online scheduling or appointment booking?"
                />
                <RadioField
                  field="needsEcommerce"
                  options={["Yes", "No", "Not sure"]}
                  label="Do you need e-commerce to sell products or services online?"
                />
                <RadioField
                  field="needsContactForm"
                  options={["Yes", "No"]}
                  label="Do you need a contact form?"
                />
                <RadioField
                  field="needsMultiLanguage"
                  options={["Yes", "No"]}
                  label="Do you need multi-language support?"
                />
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Any other features or tools you need?</Label>
                  <Textarea
                    value={formData.otherFeatures}
                    onChange={(e) => updateField("otherFeatures", e.target.value)}
                    placeholder="e.g., live chat, payment portal, client login area"
                  />
                </div>
              </>
            )}

            {/* Step 6 */}
            {currentStep === 6 && (
              <>
                <RadioField
                  field="ownsDomain"
                  options={["Yes", "No", "I'm not sure"]}
                  label="Do you own your domain name?"
                />

                {formData.ownsDomain === "Yes" && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Domain name</Label>
                      <Input
                        value={formData.domainName}
                        onChange={(e) => updateField("domainName", e.target.value)}
                        placeholder="example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Where is it registered?</Label>
                      <Select value={formData.domainRegistrar} onValueChange={(v) => updateField("domainRegistrar", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select registrar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GoDaddy">GoDaddy</SelectItem>
                          <SelectItem value="Namecheap">Namecheap</SelectItem>
                          <SelectItem value="Google Domains">Google Domains</SelectItem>
                          <SelectItem value="Cloudflare">Cloudflare</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.domainRegistrar === "Other" && (
                        <Input
                          className="mt-2"
                          value={formData.domainRegistrarOther}
                          onChange={(e) => updateField("domainRegistrarOther", e.target.value)}
                          placeholder="Where is it registered?"
                        />
                      )}
                    </div>
                  </>
                )}

                {(formData.ownsDomain === "No" || formData.ownsDomain === "I'm not sure") && (
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">
                      No worries, we'll help you get one. What domain would you want?
                    </Label>
                    <Input
                      value={formData.wantedDomain}
                      onChange={(e) => updateField("wantedDomain", e.target.value)}
                      placeholder="e.g., mybusiness.com"
                    />
                  </div>
                )}

                <RadioField
                  field="needsEmail"
                  options={["Yes", "No", "I already have them"]}
                  label="Do you need business email addresses on your domain?"
                />

                {formData.needsEmail === "Yes" && (
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">List the email addresses you need</Label>
                    <Textarea
                      value={formData.emailAddresses}
                      onChange={(e) => updateField("emailAddresses", e.target.value)}
                      placeholder="e.g., info@, john@"
                    />
                  </div>
                )}
              </>
            )}

            {/* Step 7 */}
            {currentStep === 7 && (
              <>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    What words or phrases would your ideal customer Google to find you?
                  </Label>
                  <Textarea
                    value={formData.seoKeywords}
                    onChange={(e) => updateField("seoKeywords", e.target.value)}
                    placeholder="e.g., Kansas City plumber, emergency AC repair near me"
                  />
                </div>

                <RadioField
                  field="hasGoogleBusiness"
                  options={["Yes", "No", "I'm not sure"]}
                  label="Do you have a Google Business Profile?"
                />

                <RadioField
                  field="setupAnalytics"
                  options={["Yes", "No", "I'm not sure"]}
                  label="Should we set up Google Analytics and Search Console?"
                />

                <CheckboxGroup
                  field="marketingServices"
                  label="Are you interested in any ongoing marketing services?"
                  options={[
                    "Local SEO",
                    "Monthly blog content",
                    "Google Ads",
                    "Social media ads",
                    "Not right now",
                  ]}
                />
              </>
            )}

            {/* Step 8 */}
            {currentStep === 8 && (
              <>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Who are the decision-makers on this project? *</Label>
                  <Input
                    value={formData.decisionMakers}
                    onChange={(e) => updateField("decisionMakers", e.target.value)}
                  />
                  <ErrorMsg field="decisionMakers" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Best contact name *</Label>
                  <Input
                    value={formData.contactName}
                    onChange={(e) => updateField("contactName", e.target.value)}
                  />
                  <ErrorMsg field="contactName" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Email *</Label>
                  <Input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => updateField("contactEmail", e.target.value)}
                  />
                  <ErrorMsg field="contactEmail" />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Phone *</Label>
                  <Input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => updateField("contactPhone", e.target.value)}
                  />
                  <ErrorMsg field="contactPhone" />
                </div>

                <RadioField
                  field="preferredContact"
                  options={["Email", "Phone", "Text"]}
                  label="Preferred contact method *"
                />

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Anything else we should know?</Label>
                  <Textarea
                    value={formData.anythingElse}
                    onChange={(e) => updateField("anythingElse", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Turnstile
                    ref={turnstileRef}
                    onToken={(token) => {
                      setTurnstileToken(token);
                      if (token) setVerifyError(false);
                    }}
                  />
                  {verifyError && (
                    <p className="text-sm text-destructive">
                      Please complete the verification check above and try again.
                    </p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          {currentStep > 1 ? (
            <Button variant="outline" onClick={handleBack}>
              ← Back
            </Button>
          ) : (
            <div />
          )}
          {currentStep < 8 ? (
            <Button onClick={handleNext} className="bg-[#E8A33D] text-[#081627] hover:bg-[#d9952f]">
              Next →
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-[#E8A33D] text-[#081627] hover:bg-[#d9952f]">
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          )}
        </div>
      </main>
      <style dangerouslySetInnerHTML={{ __html: ONBOARDING_THEME_CSS }} />
    </div>
  );
};

export default Onboarding;
